import type { DrugSortKey } from '@/entities/drug/api/drug.api';
import { getRouteApi } from '@tanstack/react-router';
import { DrugEmptyView } from './components/DrugEmptyView';
import type { DrugFilterValue } from './components/drugFilter';
import { DrugSearchResults } from './components/DrugSearchResults';

const route = getRouteApi('/app/drugs');

/**
 * M-11 의약품 검색 결과 — 검색은 GNB 검색창(GnbSearch)에서만 한다 (기획↔디자인 변경 912:6416 "의약품 검색 진입").
 * 검색 전 페이지(M-10)와 페이지 안 검색창은 없앴다. q 없이 들어오면 GNB 검색을 안내한다
 * (/?drug= 딥링크도 이 화면을 배경으로 드로어를 연다, M-15 D).
 * 검색어 · 필터 · 정렬 · 페이지는 모두 URL search params에 둔다 (새로고침 · 공유 시 복원).
 * GNB에서 새 검색어로 검색하면 q만 남긴 주소로 바뀌어 필터 · 정렬 · 페이지가 처음으로 돌아간다.
 * API: GET /web/drugs, /web/drugs/pharma-companies
 */
export const DrugsPage = () => {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const keyword = search.q?.trim() ?? '';
  const filter: DrugFilterValue = {
    tradable: !!search.tradable,
    filters: search.filters ?? [],
    pharma: search.pharma ?? [],
  };

  const setFilter = (next: DrugFilterValue) =>
    void navigate({
      search: (prev) => ({
        ...prev,
        tradable: next.tradable || undefined,
        filters: next.filters.length ? next.filters : undefined,
        pharma: next.pharma.length ? next.pharma : undefined,
        page: undefined,
      }),
    });

  const setSort = (sort: DrugSortKey) =>
    void navigate({
      search: (prev) => ({ ...prev, sort, page: undefined }),
    });

  const setPage = (page: number) => {
    void navigate({
      search: (prev) => ({ ...prev, page: page > 1 ? page : undefined }),
    });
    window.scrollTo({ top: 0 });
  };

  if (!keyword) {
    return (
      <DrugEmptyView
        className="min-h-[560px] py-10"
        title="찾으시는 의약품을 검색해보세요."
        description="상단 검색창에 제품명 · 성분명 · 제약사를 입력하세요."
      />
    );
  }

  return (
    <DrugSearchResults
      keyword={keyword}
      filter={filter}
      sort={search.sort ?? 'DRUG_NAME_ASC'}
      page={search.page ?? 1}
      openedDrug={search.drug}
      onFilterChange={setFilter}
      onSortChange={setSort}
      onPageChange={setPage}
    />
  );
};
